import React, { useEffect } from 'react';
import { Box, Button, ClickAwayListener, Divider, Grid, Grow, IconButton, List, ListItem, Menu, MenuItem, MenuList, Paper, Popper, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import Page from 'components/Page';
import PageHeader from 'global/PageHeader';
import { connect } from 'react-redux';
import { tokens } from 'theme/tokens';
import { CalendarTodayOutlined, MenuOutlined, PlayCircleFilledOutlined, WavingHandOutlined } from '@mui/icons-material';
import DataDialog from 'components/dialogs/DataDialog';
import { useState } from 'react';
import StatBox from 'components/StatBox';
import { Link } from 'react-router-dom';
import Notificationbar from 'global/Notificationbar';
import PieChart from 'components/Charts/piechart';
import FTIcons from 'assets/icons/FTIcons';
import { getLatestNotifications } from 'services/notification-service';
import { formatAppointment } from 'utils/formatter';
import { getLoadCountsByPeriod } from 'services/tenant-service';
import { getDispatchSummaryByDate } from 'services/home-service';
import { Calendar } from 'rsuite';
import StatusPill from 'components/StatusPill';

const HomePage = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isVideoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorRef, setAnchorRef] = useState(null);
  const [isDispatchCalendarOpen, setIsDispatchCalendarOpen] = useState(false);
  const [dispatchDate, setDispatchDate] = useState(new Date());
  const [summary, setSummary] = useState({
    loadStatusPieChart: [],
    brokerPieChart: [],
    driverPieChart: [],
  });
  const [notifications, setNotifications] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const { tenant, user, users, brokers, drivers, trucks, locations } = props;
  const activeUser = users && users.filter(u => u.email === localStorage.getItem('ACTIVE_USER_EMAIL'))[0];

  useEffect(() => {
    const startDate = new Date(dispatchDate.setHours(0, 0, 0, 0)); // dispatchDate by default is Today's date
    const endDate = new Date(dispatchDate.setHours(23, 59, 59, 999));
    const startDateISO = startDate.toISOString();
    const endDateISO = endDate.toISOString();
    
    getDispatchSummaryByDate(startDateISO).then(res => {
      if(res && res.data) {
        const apiData = res.data.data;
        const {loads, otrloads, brokers, drivers, invoices, payments} = apiData;
        const loadStatusPieChart = loads && loads['total'] !== 0 ? Object.entries(loads).map(([key, value]) => ({ id: key, label: key, value: value })) : Object.entries(otrloads).map(([key, value]) => ({ id: key, label: key, value: value }))
        const brokerPieChart = Object.entries(brokers).map(([key, value]) => ({ id: key, label: key, value: value }));
        const driverPieChart = Object.entries(drivers).map(([key, value]) => ({ id: key, label: key, value: value }));
        summary.loadStatusPieChart = loadStatusPieChart.filter(res => res.id !== 'total');
        summary.brokerPieChart = brokerPieChart.filter(res => res.id !== 'total');
        summary.driverPieChart = driverPieChart.filter(res => res.id !== 'total');
        setSummary(summary);
      }
    });
    getLatestNotifications().then(res => {
      if (res && res.data) {
        setNotifications(res.data);
      }
    });
    // getLoadCountsByPeriod(period).then(res => {
    //   console.log(res);
    // });
  }, [period, dispatchDate]);

  const brokerStats = {};
  const locationsStats = {};
  const truckStats = {};
  const driverStats = {};

  if (brokers) {
    brokerStats.count = brokers.length;
    brokerStats.active = brokers.filter(b => b.active).length;
    brokerStats.inactive = brokers.filter(b => !b.active).length;
  }
  if (locations) {
    locationsStats.count = locations.length;
    locationsStats.active = locations.filter(b => b.active).length;
    locationsStats.inactive = locations.filter(b => !b.active).length;
  }
  if (trucks) {
    truckStats.count = trucks.length;
    const miles = trucks.reduce((acc, trk) => trk.totalMilesDriven + acc, 0);
    truckStats.totalMiles = new Intl.NumberFormat('en-US', { notation: 'compact' }).format(miles);
  }
  if (drivers) {
    driverStats.count = drivers.length;
    driverStats.employee = drivers.filter(b => b.driverType === 'employee').length;
    driverStats.owner_operator = drivers.filter(b => b.driverType === 'owner_operator').length;
  }

  const playSupportVideo = (videoId) => {
    setVideoId(videoId);
    setVideoDialogOpen(true);
  };

  const handleDispatchDateChange = (evt) => {
    const dd = new Date(evt);
    const customDate = new Date(dd.setMinutes(dd.getMinutes() + dd.getTimezoneOffset())); //Need this for firefox. otherwise for chrome we can use datetime-local.
    setDispatchDate(customDate);
    setIsDispatchCalendarOpen(false);
  };

  return (
    <Page title='Home'>
      <Box display={'flex'} justifyContent={'space-between'}>
        <PageHeader
          title={tenant && tenant.locations}
          subTitle={tenant && tenant.locations ? `${tenant.city}, ${tenant.state}` : ''}
        />
        <Notificationbar />
      </Box>
      <Box>
        {activeUser &&
          <>
            <Box
              display={'flex'}
              alignItems={'center'}
            >
              <Typography color={colors.secondary.main} variant='h3' fontWeight={600}>Greetings, {activeUser.firstName} {activeUser.lastName}</Typography>
              &nbsp;&nbsp;<WavingHandOutlined color={'secondary'} sx={{ ml: 1 }} /> <br />
            </Box>
            <Typography color={colors.base.black} variant='body1'>This is your summary dashboard for quick overview.</Typography>
          </>
        }
        <Box
          pt={3}
        >
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Paper
                sx={{
                  height: '50px',
                  py: 1,
                  px: 3,
                  background: colors.main_blue[50],
                }}
              >
                <Box display={'flex'} justifyContent={'space-between'}>
                  <Typography variant='subtitle1' fontWeight={600}>Dispatch Stats</Typography>
                  <Button
                    endIcon={<CalendarTodayOutlined />}
                    onClick={(evt) => { setIsDispatchCalendarOpen(true); setAnchorRef(evt.currentTarget); }}
                  >
                    
                    <Typography variant='subtitle1' fontWeight={600}>{dispatchDate.toLocaleDateString()}</Typography>                  
                  </Button>
                </Box>
                <Popper
                  open={isDispatchCalendarOpen}
                  anchorEl={anchorRef}
                  modifiers={{
                    arrow: {
                      enabled: true,
                      element: anchorRef
                    }
                  }}
                  transition disablePortal style={{ zIndex: 1500 }}>
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                      }}
                    >
                      <Paper elevation={2}>
                        <ClickAwayListener onClickAway={() => setIsDispatchCalendarOpen(false)}>
                          <MenuList id='split-button-menu'>
                            <Calendar
                              compact
                              onSelect={handleDispatchDateChange}
                              style={{ width: 240 }}
                              value={dispatchDate}
                            />
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Paper>
              <Grid container spacing={1} mt={1} mb={1}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={{ height: 180, mt: 3 }}>
                    <PieChart
                      data={summary && summary.loadStatusPieChart || []}
                      customColor={true}
                    />
                    <Typography variant='subtitle2' fontWeight={600} margin={'auto'}>Loads</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={{ height: 180, mt: 3 }}>                
                    <PieChart
                      data={summary && summary.brokerPieChart || []}
                      customColor={false}
                    />
                    <Typography variant='subtitle2' fontWeight={600} margin={'auto'}>Broker</Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Box sx={{ height: 180, mt: 3 }}>
                    <PieChart
                      data={summary && summary.driverPieChart || []}
                      customColor={false}
                    />
                    <Typography variant='subtitle2' fontWeight={600} margin={'auto'}>Drivers</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Paper elevation={1}>
                <Box p={1} display={'flex'} justifyContent={'space-between'}>
                  <Typography variant='subtitle1' fontWeight={500}>Assets</Typography>
                  <IconButton onClick={(evt) => setAnchorEl(evt.currentTarget)}>
                    <MenuOutlined color={'primary'} />
                  </IconButton>                  
                  <Menu
                    id='long-menu'
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                  >
                    <MenuItem><Link to={'/app/brokers'}>Manage Brokers</Link></MenuItem>
                    <Divider />
                    <MenuItem><Link to={'/app/drivers'}>Manage Drivers</Link></MenuItem>
                    <Divider />
                    <MenuItem><Link to={'/app/equipments'}>Manage Trucks</Link></MenuItem>
                  </Menu>
                </Box>                
                <Divider />
                <Grid container spacing={1} p={1}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StatBox
                      title='Brokers'
                      statHighlight={brokerStats.count}
                      statBreakdown={[{ status: 'Active', value: brokerStats.active }, { status: 'In Active', value: brokerStats.inactive }]}
                      icon={<FTIcons.NavMenuCustomerIcon fill={colors.main_blue[900]}/>}
                      iconBackgroundColor={colors.main_blue[200]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StatBox
                      title='Drivers'
                      statHighlight={driverStats.count}
                      statBreakdown={[{ status: 'Regular', value: driverStats.employee }, { status: 'Owner Operator', value: driverStats.owner_operator }]}
                      icon={<FTIcons.NavMenuDriverIcon fill={colors.indigo[900]} />}
                      iconBackgroundColor={colors.indigo[200]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StatBox
                      title='Trucks'
                      statHighlight={truckStats.count}
                      statBreakdown={[{ status: 'Miles', value: truckStats.totalMiles }]}
                      icon={<FTIcons.NavMenuEquipmentIcon fill={colors.red[700]}/>}
                      iconBackgroundColor={colors.red[200]}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <StatBox
                      title='Locations'
                      statHighlight={locationsStats.count}
                      statBreakdown={[{ status: 'Active', value: locationsStats.active }, { status: 'In Active', value: locationsStats.inactive }]}
                      icon={<FTIcons.NavMenuLocationIcon fill={colors.green[900]}/>}
                      iconBackgroundColor={colors.green[200]}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
          <br/>
          <Grid container spacing={1}>
            <Grid size={{xs: 12, sm: 8}}>
              <Grid container spacing={1}>
                <Grid size={{xs: 12, sm: 4}}>
                  <Paper elevation={1}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><Typography variant='body1' fontWeight={600}>Status</Typography></TableCell>
                          <TableCell><Typography variant='body1' fontWeight={600}>Count</Typography></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.loadStatusPieChart.map((item,idx) => (
                          <TableRow key={`${idx}-${item.label}`}>
                            <TableCell>{<StatusPill status={item.label.toUpperCase()} size='medium' />}</TableCell>
                            <TableCell>{item.value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
                <Grid size={{xs: 12, sm: 4}}>
                  <Paper elevation={1}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><Typography variant='body1' fontWeight={600}>Broker</Typography></TableCell>
                          <TableCell><Typography variant='body1' fontWeight={600}>Count</Typography></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.brokerPieChart.map((item,idx) => (
                          <TableRow key={`${idx}-${item.label}`}>
                            <TableCell>{item.label}</TableCell>
                            <TableCell>{item.value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
                <Grid size={{xs: 12, sm: 4}}>
                  <Paper elevation={1}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><Typography variant='body1' fontWeight={600}>Driver</Typography></TableCell>
                          <TableCell><Typography variant='body1' fontWeight={600}>Count</Typography></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.driverPieChart.map((item,idx) => (
                          <TableRow key={`${idx}-${item.label}`}>
                            <TableCell>{item.label}</TableCell>
                            <TableCell>{item.value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{xs: 12, sm: 4}}>
              <Paper elevation={1}>
                <Typography variant='subtitle1' fontWeight={500} p={2}>Recent Activity</Typography>
                <Divider />
                {notifications.map(notify =>
                  <Box key={notify.timestamp} p={1}>
                    <Box display={'flex'} justifyContent={'space-between'}>
                      <Typography variant='subtitle2' fontWeight={600}>{notify.displayOrderId !== 0 ? notify.displayOrderId : notify.orderId}</Typography>
                      <Typography variant='body1'>{formatAppointment(notify.timestamp)}</Typography>
                    </Box>
                    <Typography variant='body1'>{notify.message}</Typography>
                    <Divider />
                  </Box>
                )}
              </Paper>              
            </Grid>
          </Grid>
          <br/>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 4 }}>              
              <Paper elevation={1} sx={{ textAlign: 'center' }}>                
                <PlayCircleFilledOutlined color='primary' />
                <Typography variant='subtitle1' fontWeight={500}>Video Tutorials</Typography>
                <Typography variant='caption'>Video tutorials to get you started with FreightSmart</Typography>
                <Divider />
                <List>
                  <ListItem>
                    <IconButton size='medium' color='primary' onClick={() => playSupportVideo('GDxrE7Zb07I')}>
                      <PlayCircleFilledOutlined />
                    </IconButton>
                    <Typography variant='body1'>{'Manage your Brokers'}</Typography>
                  </ListItem>
                  <ListItem>
                    <IconButton size='medium' color='primary' onClick={() => playSupportVideo('wnY7SP6O-B0')}>
                      <PlayCircleFilledOutlined />
                    </IconButton>
                    <Typography>{'Manage your Shippers & Consignees'}</Typography>
                  </ListItem>
                  <ListItem>
                    <IconButton size='medium' color='primary' onClick={() => playSupportVideo('-QT2xVCNO2A')}>
                      <PlayCircleFilledOutlined />
                    </IconButton>
                    <Typography>{'Manage your Drivers & Owner Operators'}</Typography>
                  </ListItem>
                  <ListItem>
                    <IconButton size='medium' color='primary' onClick={() => playSupportVideo('HPdfYoHXLDQ')}>
                      <PlayCircleFilledOutlined />
                    </IconButton>
                    <Typography>{'Manage your Assets'}</Typography>
                  </ListItem>
                  <ListItem>
                    <IconButton size='medium' color='primary' onClick={() => playSupportVideo('2e0iS-Th4Do')}>
                      <PlayCircleFilledOutlined />
                    </IconButton>
                    <Typography>{'Create Drayage or Intermodal Load'}</Typography>
                  </ListItem>
                </List>
              </Paper>
            </Grid>     
          </Grid>
        </Box>
      </Box>
      <DataDialog
        title='Help Videos'
        width='md'
        open={isVideoDialogOpen}
        onCloseHandler={() => setVideoDialogOpen(false)}
      >
        <div style={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}`}
            title='Support Video'
            frameBorder='0'
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
          />
        </div>
      </DataDialog>
    </Page>
  );
};

function mapStatesToProps(state) {
  const { tenant, user, users } = state.tenantReducer;
  const { brokers } = state.brokerReducer;
  const { drivers } = state.driverReducer;
  const { trucks } = state.truckReducer;
  const { locations } = state.locationReducer;

  return {
    tenant,
    user,
    users,
    brokers,
    drivers,
    trucks,
    locations,
  };
}

export default connect(mapStatesToProps, null)(HomePage);